import { LoadingSmall } from "@/components/LoadingSmall";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { showApiError, showSuccess } from "@/lib/toast";
import { ChannelDetailItem } from "@/lib/types";
import { deleteChannelDetailItem } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CogIcon, TrashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ChannelCalendarItemUpdateForm } from "./ChannelCalendarItemUpdateForm";
import { useState } from "react";
import { formatDateToLocal } from "@/lib/dateUtils";

export const ChannelCalendarItem = ({ event }: { event: ChannelDetailItem }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [open, setOpen] = useState(false);

  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: () => deleteChannelDetailItem(event.id, String(activeFirm?.firmGuid)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getChannelDetailItems"] });
      showSuccess(t("common.deletedSuccess"));
    },
    onError: showApiError,
  });

  const isLoading = deleteMutation.isPending;

  return (
    <>
      <div className="ml-2">
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2">
            <span className="text-sm font-medium">{t("common.event")}</span>
            <span className="text-xs text-muted-foreground">
              {formatDateToLocal(event.periodStart, false)}-{formatDateToLocal(event.periodEnd, false)}
            </span>
            {isLoading && <LoadingSmall />}
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => setOpen(true)}>
              <CogIcon className="w-4 h-4" /> {t("common.configure")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => deleteMutation.mutate()} className="text-red-500">
              <TrashIcon className="w-4 h-4" /> {t("common.deleteEntity.buttonText")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {open && <ChannelCalendarItemUpdateForm item={event} onOpenChange={setOpen} />}
    </>
  );
};
